import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase/config";
import {
  MAX_EVENT_DAYS,
  PeakEvent,
  PeakEventInput,
  addDaysYMD,
  eventLastDay,
  normaliseTags,
} from "../types/event";
import { PerformanceLog, Session, TrainingLog } from "../types/post";
import { dedupeTags } from "../utils/eventTagUtils";

/** Firestore `in` queries accept at most 10 values. */
const IN_CHUNK = 10;

export function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/** Drop undefined / null so nothing undefined reaches Firestore. */
function compact<T extends Record<string, any>>(obj: T): Partial<T> {
  const out: Record<string, any> = {};
  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    if (value !== undefined && value !== null) out[key] = value;
  });
  return out as Partial<T>;
}

/** Cap an event to MAX_EVENT_DAYS, and drop `endDate` unless it is > date. */
export function normaliseEventDates(
  date: string,
  endDate: string | undefined,
): { date: string; endDate?: string } {
  if (!endDate || endDate <= date) return { date };
  const cap = addDaysYMD(date, MAX_EVENT_DAYS - 1);
  return { date, endDate: endDate > cap ? cap : endDate };
}

/** Build a PeakEvent from a Firestore snapshot's data. */
export function eventFromData(id: string, data: Record<string, any>): PeakEvent {
  // `priority` was removed from events; old documents may still carry it, so
  // it is dropped on read.
  const { priority: _legacyPriority, ...rest } = data;
  const event: PeakEvent = {
    ...(rest as Omit<PeakEvent, "id" | "tags">),
    id,
    createdAt: data.createdAt ?? 0,
    updatedAt: data.updatedAt ?? 0,
  };
  const tags = normaliseTags(data.tags);
  if (tags) event.tags = tags;
  else delete event.tags;
  return event;
}

/**
 * Client-side overlap filter for the window query (Events.md §3.1): keeps
 * events whose `[date, lastDay]` overlaps `[start, end]`.
 */
export function filterEventsOverlapping(
  events: PeakEvent[],
  start: string,
  end: string,
): PeakEvent[] {
  return events.filter((e) => e.date <= end && eventLastDay(e) >= start);
}

/** Sort ascending by start date, then name. */
export function sortEvents(events: PeakEvent[]): PeakEvent[] {
  return [...events].sort(
    (a, b) => a.date.localeCompare(b.date) || a.name.localeCompare(b.name),
  );
}

const eventsCol = (uid: string) => collection(db, "events", uid, "events");
const eventRef = (uid: string, id: string) => doc(db, "events", uid, "events", id);

/** Create an athlete-authored event; returns the new event id. */
export async function createEvent(
  uid: string,
  input: PeakEventInput,
): Promise<string> {
  if (!uid) throw new Error("User ID is required");
  const now = Date.now();
  const dates = normaliseEventDates(input.date, input.endDate);
  const tags = dedupeTags(input.tags);
  const body = compact({
    name: input.name.trim(),
    type: input.type,
    ...dates,
    notes: input.notes?.trim() || undefined,
    sport: input.sport?.trim() || undefined,
    // Omit the key entirely when there are no tags.
    tags: tags.length > 0 ? tags : undefined,
    status: input.status,
    createdAt: now,
    updatedAt: now,
  });
  const ref = await addDoc(eventsCol(uid), body);
  return ref.id;
}

/**
 * Update an event. Keys present in `patch` with value `undefined` (or an
 * empty optional string / tag list) are removed from the document; absent keys
 * are left untouched so unknown keys round-trip.
 */
export async function updateEvent(
  uid: string,
  eventId: string,
  patch: Partial<PeakEventInput>,
): Promise<void> {
  if (!uid || !eventId) throw new Error("User ID and event ID are required");
  const out: Record<string, any> = { updatedAt: Date.now() };
  const clearable = ["notes", "sport", "status", "endDate"] as const;

  (Object.keys(patch) as (keyof PeakEventInput)[]).forEach((key) => {
    if (key === "tags") {
      // Present key: write the cleaned list, or clear it when emptied.
      const tags = dedupeTags(patch.tags);
      out.tags = tags.length > 0 ? tags : deleteField();
      return;
    }
    let value: any = patch[key];
    if (typeof value === "string") value = value.trim();
    if (key === "name" || key === "type" || key === "date") {
      if (value !== undefined && value !== "") out[key] = value;
      return;
    }
    if ((clearable as readonly string[]).includes(key)) {
      out[key] = value === undefined || value === "" ? deleteField() : value;
    }
  });

  // Keep the date range consistent with the 31-day cap.
  if (out.date || "endDate" in patch) {
    const needsCurrent = !out.date || !("endDate" in patch);
    const current = needsCurrent ? await getEvent(uid, eventId) : null;
    const date: string | undefined = out.date ?? current?.date;
    const endDate: string | undefined =
      "endDate" in patch ? patch.endDate : current?.endDate;
    if (date) {
      const normalised = normaliseEventDates(date, endDate);
      out.endDate = normalised.endDate ?? deleteField();
    }
  }

  await updateDoc(eventRef(uid, eventId), out);
}

/**
 * Delete an event and (best effort) clear `eventId` / `eventDay` on linked
 * training logs, results and sessions (Events.md §3.2).
 */
export async function deleteEvent(uid: string, eventId: string): Promise<void> {
  if (!uid || !eventId) throw new Error("User ID and event ID are required");

  try {
    const [logsSnap, resultsSnap, sessionsSnap] = await Promise.all([
      getDocs(query(collection(db, "trainingLogs", uid, "logs"), where("eventId", "==", eventId))),
      getDocs(query(collection(db, "performanceLogs", uid, "logs"), where("eventId", "==", eventId))),
      getDocs(query(collection(db, "sessions", uid, "sessions"), where("eventId", "==", eventId))),
    ]);
    const refs: { ref: any; patch: Record<string, any> }[] = [
      ...logsSnap.docs.map((d) => ({
        ref: d.ref,
        patch: { eventId: deleteField(), eventDay: deleteField() },
      })),
      ...resultsSnap.docs.map((d) => ({ ref: d.ref, patch: { eventId: deleteField() } })),
      ...sessionsSnap.docs.map((d) => ({ ref: d.ref, patch: { eventId: deleteField() } })),
    ];
    for (const group of chunk(refs, 400)) {
      const batch = writeBatch(db);
      group.forEach(({ ref, patch }) => batch.update(ref, patch));
      await batch.commit();
    }
  } catch (error) {
    console.error("Error clearing event links (best effort):", error);
  }

  await deleteDoc(eventRef(uid, eventId));
}

export async function getEvent(uid: string, eventId: string): Promise<PeakEvent | null> {
  if (!uid || !eventId) return null;
  try {
    const snap = await getDoc(eventRef(uid, eventId));
    return snap.exists() ? eventFromData(snap.id, snap.data()) : null;
  } catch (error: any) {
    if (error?.code === "permission-denied") return null;
    console.error("Error getting event:", error);
    throw error;
  }
}

/** Every event of the athlete (events per athlete are few). */
export async function getAllEvents(uid: string): Promise<PeakEvent[]> {
  if (!uid) return [];
  try {
    const snap = await getDocs(eventsCol(uid));
    return sortEvents(snap.docs.map((d) => eventFromData(d.id, d.data())));
  } catch (error: any) {
    if (error?.code === "permission-denied") return [];
    console.error("Error getting events:", error);
    throw error;
  }
}

/**
 * Events overlapping `[start, end]` (YYYYMMDD) — Events.md §3.1. Range query
 * on `date` from `start - 31d` to `end`, then filtered by overlap.
 */
export async function getEventsInWindow(
  uid: string,
  start: string,
  end: string,
): Promise<PeakEvent[]> {
  if (!uid) return [];
  try {
    const q = query(
      eventsCol(uid),
      where("date", ">=", addDaysYMD(start, -MAX_EVENT_DAYS)),
      where("date", "<=", end),
    );
    const snap = await getDocs(q);
    return sortEvents(
      filterEventsOverlapping(
        snap.docs.map((d) => eventFromData(d.id, d.data())),
        start,
        end,
      ),
    );
  } catch (error: any) {
    if (error?.code === "permission-denied") return [];
    console.error("Error getting events in window:", error);
    throw error;
  }
}

/** Sessions linked to an event (`session.eventId == id`). */
export async function getLinkedSessions(
  uid: string,
  eventId: string,
): Promise<(Session & { id: string })[]> {
  if (!uid || !eventId) return [];
  try {
    const snap = await getDocs(
      query(collection(db, "sessions", uid, "sessions"), where("eventId", "==", eventId)),
    );
    return snap.docs
      .map((d) => ({ ...(d.data() as Session), id: d.id }))
      .sort((a, b) => (a.plannedDate ?? "").localeCompare(b.plannedDate ?? ""));
  } catch (error: any) {
    if (error?.code === "permission-denied") return [];
    console.error("Error getting linked sessions:", error);
    throw error;
  }
}

export interface EventPerformance {
  trainingLog: TrainingLog & { id: string };
  results: (PerformanceLog & { id: string })[];
}

/**
 * Linked performances (Events.md §3.1): training logs where
 * `eventId == id`, then results by `linkedTrainingLogID in [...]` (chunks of 10).
 */
export async function getPerformancesForEvent(
  uid: string,
  eventId: string,
): Promise<EventPerformance[]> {
  if (!uid || !eventId) return [];
  try {
    const logsSnap = await getDocs(
      query(collection(db, "trainingLogs", uid, "logs"), where("eventId", "==", eventId)),
    );
    const logs = logsSnap.docs
      .map((d) => ({ ...(d.data() as TrainingLog), id: d.id }))
      .sort((a, b) => (a.dateID ?? "").localeCompare(b.dateID ?? ""));
    if (logs.length === 0) return [];

    const results: (PerformanceLog & { id: string })[] = [];
    for (const ids of chunk(
      logs.map((l) => l.id),
      IN_CHUNK,
    )) {
      const snap = await getDocs(
        query(
          collection(db, "performanceLogs", uid, "logs"),
          where("linkedTrainingLogID", "in", ids),
        ),
      );
      snap.docs.forEach((d) => results.push({ ...(d.data() as PerformanceLog), id: d.id }));
    }
    results.sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0));
    return logs.map((trainingLog) => ({
      trainingLog,
      results: results.filter((r) => r.linkedTrainingLogID === trainingLog.id),
    }));
  } catch (error: any) {
    if (error?.code === "permission-denied") return [];
    console.error("Error getting performances for event:", error);
    throw error;
  }
}

/** Training logs that mark `eventId` as logged (derived "Logged" state). */
export async function getLoggedEventIds(
  uid: string,
  events: PeakEvent[],
): Promise<Set<string>> {
  const logged = new Set<string>();
  if (!uid || events.length === 0) return logged;
  try {
    const ids = events.map((e) => e.id);
    for (const group of chunk(ids, IN_CHUNK)) {
      const snap = await getDocs(
        query(collection(db, "trainingLogs", uid, "logs"), where("eventId", "in", group)),
      );
      snap.docs.forEach((d) => {
        const id = (d.data() as TrainingLog).eventId;
        if (id) logged.add(id);
      });
    }
  } catch (error: any) {
    if (error?.code !== "permission-denied") {
      console.error("Error getting logged event ids:", error);
    }
  }
  return logged;
}
